/**
 * Alfred's onboarding (Connectors › Outreach → Connect): "Alfred's number", "Forward your business calls", then
 * "Alfred is ready", and the rules that decide what each one shows. Import-free so jest can exercise it, and mirrored
 * 1:1 by iOS `AlfredSetup.swift` and Android `AlfredSetup.kt` — same steps, same default, same rules.
 *
 * The state is the server's (`/api/connectors/alfred-setup`, native-data `alfred-setup-state`): nothing here
 * decides whether Alfred is connected, only how a state it was handed is drawn.
 */

export type AlfredSetupConnection = 'DISCONNECTED' | 'NEEDS_RECONNECT' | 'VALID';

/** One way of forwarding, as dialed from the business's own phone, and the code that undoes it. */
export interface AlfredDialCode {
  /** The GSM code: AT&T, T-Mobile and most other phones. */
  standard: string;
  verizon: string;
  /** Undo codes — sent by a server that has them; the settings screen's turn-off line needs both. */
  standardOff?: string;
  verizonOff?: string;
}

export interface AlfredForwardingDialCodes {
  always: AlfredDialCode;
  unanswered: AlfredDialCode;
}

/** How callers hear Alfred now. A field Alfred holds no value for is `null`. */
export interface AlfredSetupVoice {
  assistantName: string | null;
  tone: 'WARM' | 'PROFESSIONAL' | 'ENERGETIC' | 'CALM' | null;
  gender: 'MALE' | 'FEMALE' | null;
  greeting: string | null;
}

export interface AlfredSetupState {
  connection: AlfredSetupConnection;
  /** E.164 — what Copy copies and the test call dials. */
  phoneNumber: string | null;
  /** The same number written for reading. */
  displayPhoneNumber: string | null;
  forwarding: AlfredForwardingDialCodes | null;
  /** `null` when Alfred is not on the site, and from a server older than the field. */
  voice?: AlfredSetupVoice | null;
}

/** Number → Routing → Ready. The last is Alfred's own "Alfred is ready" moment: a test call, and what to change in chat. */
export type AlfredSetupStep = 'number' | 'routing' | 'ready';

/** The steps the counter counts. "Alfred is ready" is where the setup lands, not a third thing to do. */
export const ALFRED_SETUP_STEPS: readonly AlfredSetupStep[] = ['number', 'routing'];

export type AlfredForwardingMode = 'always' | 'unanswered';

export const ALFRED_FORWARDING_MODES: readonly AlfredForwardingMode[] = ['always', 'unanswered'];

/** Ring the owner first: the gentler change to how their line behaves, and the card the design selects. */
export const DEFAULT_FORWARDING_MODE: AlfredForwardingMode = 'unanswered';

/**
 * Whether step 1 still owes the "Get my number" consent. True until Alfred answers on a number: a paused or
 * switched-off line (NEEDS_RECONNECT) goes through it too, because the tap is the yes to the per-call
 * charges, and the server's setup reuses the number the site already holds.
 */
export function needsSetup(state: AlfredSetupState): boolean {
  return state.connection !== 'VALID' || !state.phoneNumber;
}

/** "Step {current} of {total}"; `null` on the ready step, which carries no counter. */
export function stepPosition(step: AlfredSetupStep): { current: number; total: number } | null {
  const index = ALFRED_SETUP_STEPS.indexOf(step);
  return index < 0 ? null : { current: index + 1, total: ALFRED_SETUP_STEPS.length };
}

/** How the reader left the routing step: with a way of forwarding chosen, or with "Do This Later". */
export type AlfredRouting = AlfredForwardingMode | 'skipped';

export interface AlfredReadyCopy {
  subtitleKey: 'ready.subtitle' | 'ready.subtitleSkipped';
  /** How to check that forwarding works — a call to the business line from another phone — once the reader
   *  chose a way of forwarding. `null` when forwarding was put off. Nothing can confirm forwarding for them:
   *  the code runs on their carrier, and no Alfred call log says a call was forwarded. */
  forwardingCheckKey: 'ready.testCall.always' | 'ready.testCall.unanswered' | null;
  /** "Set Up Forwarding", back to the routing step. */
  offersForwarding: boolean;
}

/**
 * The ready step's copy keys (under the setup prefix) and its optional action, from how the routing step ended.
 * The test call to Alfred's own number ("Call {number}", `ready.testCall.description`) is offered either way:
 * it is what callers hear, whether or not forwarding is on.
 */
export function readyCopy(routing: AlfredRouting): AlfredReadyCopy {
  if (routing === 'skipped') {
    return { subtitleKey: 'ready.subtitleSkipped', forwardingCheckKey: null, offersForwarding: true };
  }
  return { subtitleKey: 'ready.subtitle', forwardingCheckKey: `ready.testCall.${routing}`, offersForwarding: false };
}

/** The codes the hint names for the chosen way of forwarding; `null` with no number to forward to. */
export function dialCodes(
  forwarding: AlfredForwardingDialCodes | null,
  mode: AlfredForwardingMode,
): { code: string; verizonCode: string } | null {
  if (!forwarding) return null;
  return { code: forwarding[mode].standard, verizonCode: forwarding[mode].verizon };
}

/** Dials Alfred's own number. A plain number, so iOS opens it too — unlike a forwarding code's `*` and `#`. */
export function testCallUrl(phoneNumber: string): string {
  return `tel:${phoneNumber.trim()}`;
}

/** "Alfred · Warm tone · Male voice": the name (Alfred's own default when unnamed), then whichever labels Alfred holds. */
export function voiceSummary(voice: AlfredSetupVoice, toneLabel: string | null, genderLabel: string | null): string {
  return [voice.assistantName ?? 'Alfred', toneLabel, genderLabel].filter((part): part is string => !!part).join(' · ');
}
